import SearchRounded from '@mui/icons-material/SearchRounded';
import {
  Box,
  Card,
  InputAdornment,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
} from '@mui/material';
import { useEffect, useState, type ReactNode } from 'react';

export interface Column<T> {
  key: string;
  header: ReactNode;
  render: (row: T) => ReactNode;
  align?: 'left' | 'right' | 'center';
  width?: number | string;
  /** Hidden below this breakpoint (keeps phone tables readable). */
  hideBelow?: 'sm' | 'md' | 'lg';
}

export interface PageInfo {
  page: number;
  pageSize: number;
  total: number;
}

/**
 * The standard list: a card with an optional toolbar (search + filters), a table with skeleton rows,
 * an empty state, and server-side pagination (pages are 1-based, as in the API).
 */
export function DataTable<T>({
  columns,
  rows,
  rowKey,
  loading,
  page,
  onPageChange,
  onPageSizeChange,
  onRowClick,
  empty,
  toolbar,
}: {
  columns: Column<T>[];
  rows: T[] | undefined;
  rowKey: (row: T) => string;
  loading?: boolean;
  page?: PageInfo;
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  onRowClick?: (row: T) => void;
  empty?: ReactNode;
  toolbar?: ReactNode;
}) {
  const display = (c: Column<T>) =>
    c.hideBelow ? { display: { xs: 'none', [c.hideBelow]: 'table-cell' } } : {};
  return (
    <Card>
      {toolbar && (
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1.5}
          sx={{ p: 2, borderBottom: 1, borderColor: 'divider', alignItems: { sm: 'center' } }}
        >
          {toolbar}
        </Stack>
      )}
      <TableContainer>
        <Table size="medium">
          <TableHead>
            <TableRow>
              {columns.map((c) => (
                <TableCell key={c.key} align={c.align} sx={{ width: c.width, ...display(c) }}>
                  {c.header}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading &&
              Array.from({ length: 6 }, (_, i) => (
                <TableRow key={`s${String(i)}`}>
                  {columns.map((c) => (
                    <TableCell key={c.key} sx={display(c)}>
                      <Skeleton
                        width={c.align === 'right' ? '50%' : '70%'}
                        sx={{ ml: c.align === 'right' ? 'auto' : 0 }}
                      />
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            {!loading &&
              rows?.map((row) => (
                <TableRow
                  key={rowKey(row)}
                  hover
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  // Clickable rows open with Enter or Space too, like a link.
                  tabIndex={onRowClick ? 0 : undefined}
                  onKeyDown={
                    onRowClick
                      ? (e) => {
                          if (e.target !== e.currentTarget) return;
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            onRowClick(row);
                          }
                        }
                      : undefined
                  }
                  sx={{
                    cursor: onRowClick ? 'pointer' : 'default',
                    '&:last-child td': { borderBottom: 0 },
                    '&:focus-visible': {
                      outline: '2px solid',
                      outlineColor: 'primary.main',
                      outlineOffset: -2,
                    },
                  }}
                >
                  {columns.map((c) => (
                    <TableCell key={c.key} align={c.align} sx={display(c)}>
                      {c.render(row)}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>
      {!loading && rows?.length === 0 && <Box>{empty}</Box>}
      {page && onPageChange && page.total > 0 && (
        <TablePagination
          component="div"
          count={page.total}
          page={page.page - 1}
          rowsPerPage={page.pageSize}
          rowsPerPageOptions={[10, 25, 50, 100]}
          onPageChange={(_, p) => onPageChange(p + 1)}
          onRowsPerPageChange={(e) => onPageSizeChange?.(Number(e.target.value))}
          sx={{ borderTop: 1, borderColor: 'divider' }}
        />
      )}
    </Card>
  );
}

/** Debounced search box for list toolbars. */
export function SearchField({
  value,
  onChange,
  placeholder = 'Search…',
  delay = 300,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  delay?: number;
}) {
  const [text, setText] = useState(value);
  useEffect(() => {
    if (text === value) return;
    const t = setTimeout(() => onChange(text), delay);
    return () => clearTimeout(t);
  }, [text, value, onChange, delay]);
  return (
    <TextField
      value={text}
      onChange={(e) => setText(e.target.value)}
      placeholder={placeholder}
      sx={{ maxWidth: { sm: 340 } }}
      slotProps={{
        input: {
          startAdornment: (
            <InputAdornment position="start">
              <SearchRounded fontSize="small" />
            </InputAdornment>
          ),
        },
      }}
    />
  );
}
